import React, { useEffect, useMemo, useState } from "react";
import { Loader2 } from "lucide-react";
import { dataTrigger } from "@/analytics";
import { fetchAppFeatures } from "@/api/app";
import { registerWithEmail, resendVerification } from "@/api/auth";
import { EmailCodeStep } from "./EmailCodeStep";
import { Button } from "@/ui/Button/Button";
import cls from "./styles.module.scss";

interface EmailRegisterFormProps {
  onSwitchToLogin: () => void;
  /** Показан экран «Проверьте почту» (true) или форма (false). */
  onCodeStep?: (on: boolean) => void;
}

export function EmailRegisterForm({ onSwitchToLogin, onCodeStep }: EmailRegisterFormProps) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");

  const [error, setError] = useState("");
  const [status, setStatus] = useState("");
  const [loading, setLoading] = useState(false);

  const [registeredEmail, setRegisteredEmail] = useState<string | null>(null);
  const [emailCode, setEmailCode] = useState(false);

  useEffect(() => {
    void fetchAppFeatures().then((f) => setEmailCode(Boolean(f.email_code)));
  }, []);

  const isEmailValid = useMemo(() => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email), [
    email,
  ]);

  const canSubmit = isEmailValid && password.length >= 6 && !loading;

  const onSubmit = async (e: React.SubmitEvent) => {
    e.preventDefault();
    setError("");
    setStatus("");

    if (!email) return setError("Введите email");
    if (!isEmailValid) return setError("Введите корректный email");
    if (!password) return setError("Введите пароль");
    if (password.length < 6) {
      return setError("Пароль должен быть минимум 6 символов");
    }

    try {
      setLoading(true);
      await registerWithEmail(email, password);

      setRegisteredEmail(email);
      setStatus(
        "Мы отправили письмо для подтверждения email. Откройте ссылку в письме."
      );
    } catch (err) {
      const data = (err as { response?: { status?: number; data?: { error?: string } } })?.response;
      setError(
        data?.status === 429 && data.data?.error
          ? data.data.error
          : "Ошибка регистрации. Проверьте данные и попробуйте снова."
      );
    } finally {
      setLoading(false);
    }
  };

  const onResend = async () => {
    setError("");
    setStatus("");

    if (!registeredEmail) return;

    try {
      setLoading(true);
      await resendVerification(registeredEmail);
      setStatus("Письмо отправлено повторно.");
    } catch {
      setError("Не удалось отправить письмо. Попробуйте позже.");
    } finally {
      setLoading(false);
    }
  };

  const emailDisabled = Boolean(registeredEmail);
  const passwordDisabled = Boolean(registeredEmail);
  const loaderIcon = loading ? (
    <Loader2 className={cls.loaderIcon} />
  ) : undefined;

  const inCode = Boolean(registeredEmail && emailCode);
  useEffect(() => {
    onCodeStep?.(inCode);
  }, [inCode, onCodeStep]);

  if (registeredEmail && emailCode) {
    return (
      <EmailCodeStep
        email={registeredEmail}
        purpose="confirm"
        onChangeEmail={() => {
          setRegisteredEmail(null);
          setStatus("");
        }}
      />
    );
  }

  return (
    <form onSubmit={onSubmit} className={cls.form}>
      <div className={cls.field}>
        <label className={cls.label}>Email</label>
        <input
          className={cls.input}
          type="email"
          value={email}
          onChange={(e) => {
            setEmail(e.target.value);
            setError("");
            setStatus("");
            setRegisteredEmail(null);
          }}
          placeholder="example@mail.com"
          autoComplete="email"
          disabled={emailDisabled}
        />
      </div>

      <div className={cls.field}>
        <label className={cls.label}>Пароль</label>
        <input
          className={cls.input}
          type="password"
          value={password}
          onChange={(e) => {
            setPassword(e.target.value);
            setError("");
            setStatus("");
            setRegisteredEmail(null);
          }}
          placeholder="••••••••"
          autoComplete="new-password"
          disabled={passwordDisabled}
        />
      </div>

      {error ? <div className={cls.error}>{error}</div> : null}
      {status ? <div className={cls.status}>{status}</div> : null}

      {!registeredEmail ? (
        <Button
          variant="primary"
          size="lg"
          type="submit"
          disabled={!canSubmit}
          aria-busy={loading}
          leftIcon={loaderIcon}
          {...dataTrigger("bkn.auth.register")}
        >
          {loading ? "Подождите..." : "Зарегистрироваться"}
        </Button>
      ) : (
        <>
          <Button
            variant="secondary"
            size="md"
            type="button"
            onClick={onResend}
            disabled={loading}
            aria-busy={loading}
            leftIcon={loaderIcon}
            {...dataTrigger("bkn.auth.resend_email")}
          >
            {loading ? "Отправляем..." : "Отправить письмо повторно"}
          </Button>

          <Button
            variant="primary"
            size="lg"
            type="button"
            onClick={onSwitchToLogin}
            disabled={loading}
            {...dataTrigger("bkn.auth.go_login")}
          >
            Перейти ко входу
          </Button>
        </>
      )}

      <div className={cls.switchRow}>
        <button
          type="button"
          className={cls.linkBtn}
          onClick={onSwitchToLogin}
          {...dataTrigger("bkn.auth.to_login")}
        >
          Уже есть аккаунт? Войти
        </button>
      </div>
    </form>
  );
}
